import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BsHandbag } from "react-icons/bs";
import { IoCartOutline } from "react-icons/io5";
import GalleryItemShimmer from "../../containers/Gallery/item.shimmer";
import classes from "../../containers/Gallery/item.module.css";
import { useAddToCartButton } from "../CartPage/useAddToCartButton";
import { useAddCompareList } from "../ComparePage/useAddCompareList";
import { useCompare } from "../../context/CompareContext";

const SUPPORTED_PRODUCT_TYPES = [
  "SimpleProduct",
  "ConfigurableProduct",
  "BundleProduct",
];

export const useGalleryItem = (props = {}) => {
  const { item, handleCart, setSelectedItem, buyNow, setBuyNow } = props;
  const { data: compareData } = useCompare();
  const location = useLocation();
  const navigate = useNavigate();

  const isAddedToCompare = useMemo(
    () => compareData?.some((prod) => prod.product.id === item.id) || false,
    [compareData, item.id]
  );

  const productType = item?.__typename || null;
  const isSupportedProductType = SUPPORTED_PRODUCT_TYPES.includes(productType);

  const { isInStock } = useAddToCartButton({
    item,
    buyNow,
  });

  const handleCheck = () => {
    setSelectedItem(item);
    handleCart(item.sku);
  };

  const { addToCompare } = useAddCompareList({ productId: item.id });

  if (!item) {
    return <GalleryItemShimmer classes={classes} />;
  }

  const addButton = isSupportedProductType &&
    isInStock &&
    item.__typename !== "ConfigurableProduct" &&
    item.__typename !== "BundleProduct" &&
    !item.enable_site_visit &&
    !item.enable_appliance_site_visit && (
      <button aria-label="Add to Cart" onClick={handleCheck} type="button">
        <BsHandbag size={20} />
      </button>
    );

  const buyNowButton =
    isSupportedProductType &&
    (isInStock ? (
      <button
        type="button"
        aria-label="Buy Now"
        onClick={() => {
          if (
            item.__typename === "ConfigurableProduct" ||
            item.__typename === "BundleProduct" ||
            item.enable_site_visit ||
            item.enable_appliance_site_visit
          ) {
            navigate(`/product/${item.url_key}.html`);
          } else {
            handleCheck();
            setBuyNow(true);
          }
        }}
        style={{ display: "flex", alignItems: "center", gap: "4px" }}
      >
        {item.__typename === "ConfigurableProduct" ||
        item.__typename === "BundleProduct" ||
        item.enable_site_visit ||
        item.enable_appliance_site_visit ? (
          "View Details"
        ) : (
          <>
            <IoCartOutline />
            Buy Now
          </>
        )}
      </button>
    ) : (
      <button aria-label="Buy Now" type="button">
        Out Of Stock
      </button>
    ));

  const value = useMemo(() => {
    if (item.__typename.toLowerCase() === "bundleproduct") {
      const selectedBundle = item.items[0]?.option_id;
      if (selectedBundle) {
        const bundleOptions = item.items.find(
          (pd) => pd.option_id === selectedBundle
        );
        const totalPrice = bundleOptions.options.reduce(
          (acc, curr) =>
            acc +
            curr.product.price_range.maximum_price.regular_price.value *
              curr.quantity,
          0
        );

        const afterDiscount = bundleOptions.options.reduce((acc, curr) => {
          return (
            acc +
            curr.product.price_range.maximum_price.final_price.value *
              curr.quantity
          );
        }, 0);

        return {
          regularPrice: { value: totalPrice },
          discountPrice: { value: afterDiscount },
        };
      } else {
        const regularPrice = item.price_range.maximum_price.regular_price;
        const discountPrice =
          item.price_range.maximum_price.discount?.amount_off &&
          item.price_range.maximum_price.final_price;

        return {
          regularPrice,
          discountPrice,
        };
      }
    }

    const regularPrice = item.price_range.minimum_price.regular_price;
    const discountPrice =
      item.price_range.minimum_price.discount?.amount_off &&
      item.price_range.minimum_price.final_price;

    return {
      regularPrice,
      discountPrice,
    };
  }, [item]);
  const { discountPrice, regularPrice } = value;

  return {
    addButton,
    regularPrice,
    discountPrice,
    addToCompare,
    buyNowButton,
    isAddedToCompare,
    location,
  };
};
